import React from "react";
import {
  Camera,
  ClipboardList,
  House,
  MessageSquare,
  PhoneCall,
  ShieldCheck,
  UserCheck,
  UserCog,
  UserPlus,
} from "lucide-react";
import card1Video from "../assets/animation/card1.mp4";
import card2Video from "../assets/animation/card2.mp4";
import card3Video from "../assets/animation/card3.mp4";
import card4Video from "../assets/animation/card4.mp4";
import card5Video from "../assets/animation/card5.mp4";
import card7Video from "../assets/animation/card7.mp4";

const COLORS = {
  primary: "#3AAFA9",
  textMain: "#2E2E2E",
  textSub: "#4B5563",
  textMuted: "#9CA3AF",
  line: "#E6E8EC",
  error: "#EF4444",
};

// const BLUE = "#2f62df";
const BLUE = COLORS.primary; 
const LIGHT_BLUE = "#52dfd8";
const BG = "#f2f2f3";
const TEXT_DARK = "#0f1b35";
const TEXT_BODY = "#334e6d";

const steps = [
  {
    step: "Step 1",
    title: "Choose a Plan",
    desc: "Select the subscription plan that fits your parents' needs - from basic monitoring to comprehensive care.",
    image: card1Video,
    Icon: ClipboardList,
  },
  {
    step: "Step 2",
    title: "Parent Profile Setup",
    desc: "We understand your parents' routine, medical conditions, preferences, and family contact details.",
    image: card2Video,
    Icon: UserPlus,
  },
  {
    step: "Step 3",
    title: "Care Partner Assignment",
    desc: "A verified local care partner is assigned within 48 hours. You get their full profile and credentials.",
    image: card3Video,
    Icon: UserCheck,
  },
  {
    step: "Step 4",
    title: "First Visit & Assessment",
    desc: "Initial home visit to meet your parents, understand their environment, and establish trust.",
    image: card4Video,
    Icon: House,
  },
  {
    step: "Step 5",
    title: "Daily Monitoring Begins",
    desc: "Scheduled daily or weekly visits with photo and video updates sent to you in real-time.",
    image: card5Video,
    Icon: Camera,
  },
  {
    step: "Step 6",
    title: "Ongoing Support",
    desc: "Your dedicated account manager coordinates all care needs, doctor visits, and special requests.",
    image: card7Video,
    Icon: UserCog,
  },
];

const afterBooking = [
  {
    title: "Transparent Communication",
    desc: "Direct WhatsApp group with you, your parents, care partner, and account manager.",
    Icon: MessageSquare,
  },
  {
    title: "Photo & Video Updates",
    desc: "Every visit is documented with timestamps and clear visibility into daily care activities.",
    Icon: Camera,
  },
  {
    title: "Direct Support Line",
    desc: "Call your account manager anytime for concerns, updates, or special requests.",
    Icon: PhoneCall,
  },
  {
    title: "Quality Accountability",
    desc: "Regular feedback collection and service quality monitoring for continuous improvement.",
    Icon: ShieldCheck,
  },
];

const StepCard = ({ item }) => {
  const Icon = item.Icon;

  return (
    <div
      className="step-card group relative overflow-hidden w-full w-full max-w-[520px]
 md:min-h-[380px] rounded-[30px] p-6 sm:p-7 border transition-all duration-500"
      style={{
        borderColor: "#d5e2f3",
        background: "linear-gradient(135deg, #ffffff 0%, #f2f7ff 50%, #fff4e8 100%)",
        boxShadow: "0 16px 40px rgba(17, 35, 62, 0.14)",
      }}
    >
      <div className="step-card-orb-a absolute -top-16 -right-10 w-48 h-48 rounded-full bg-[#f3ddd2]/75 blur-[2px]" />
      <div className="step-card-orb-b absolute -bottom-16 -left-12 w-44 h-44 rounded-full bg-[#dce8ff]/75 blur-[2px]" />
      <div
        className="absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(circle at 20% 12%, rgba(255,255,255,0.86) 0%, rgba(255,255,255,0) 48%)",
        }}
      />

      <div className="relative z-10">
        <div
          className="inline-flex text-sm font-semibold px-4 py-1.5 rounded-full border"
          style={{ color: BLUE, borderColor: "#bfd5ff", backgroundColor: "#edf4ff" }}
        >
          {item.step}
        </div>
        <h3 className="mt-4 text-[1.8rem] sm:text-[2.05rem] leading-tight font-bold" style={{ color: TEXT_DARK }}>
          {item.title}
        </h3>
        <p className="mt-3 text-[1.05rem] sm:text-[1.15rem] leading-[1.9]" style={{ color: TEXT_BODY }}>
          {item.desc}
        </p>

        <div
          className="step-card-media mt-6 rounded-2xl border overflow-hidden h-[160px] sm:h-[200px] lg:h-[230px]
"
          style={{
            borderColor: "#d5dfef",
            backgroundColor: "rgba(255,255,255,0.8)",
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.72), 0 10px 20px rgba(27, 44, 72, 0.1)",
          }}
        >
          {item.image ? (
            <video
              src={item.image}
              className="w-full h-full object-cover step-card-video"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center gap-3" style={{ color: BLUE }}>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "#e7efff" }}>
                <Icon size={30} />
              </div>
              <p className="text-base sm:text-lg font-semibold">Add step image here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const HowItWork = () => {
  return (
    <div style={{ backgroundColor: BG }}>
      <section
        className="hero-premium relative overflow-hidden px-4 py-12 sm:py-16 text-center"
          style={{
            background:
              "radial-gradient(circle at 14% 10%, #e9f8f7 0%, #dff4f2 38%, #d3efec 68%, #bee5e1 100%)",
          }}
      >
        <div className="hero-premium-orb-1 pointer-events-none absolute -top-24 -left-20 w-[360px] h-[360px] rounded-full blur-3xl opacity-80" />
        <div className="hero-premium-orb-2 pointer-events-none absolute top-16 -right-20 w-[400px] h-[400px] rounded-full blur-3xl opacity-75" />
        <div className="hero-premium-grid pointer-events-none absolute inset-0 opacity-40" />

        <span className="hero-premium-pill relative inline-block text-sm sm:text-base font-semibold rounded-full px-5 py-2 mb-5 sm:mb-6">
          Simple & Transparent
        </span>
        <h1 className="hero-premium-title text-[2rem] sm:text-[3rem] lg:text-[3.6rem] font-bold tracking-[-0.02em]">
          How JANAK Works
        </h1>
        <p className="hero-premium-sub text-[1rem] sm:text-[1.3rem] lg:text-[1.5rem] max-w-4xl mx-auto mt-4 sm:mt-5 leading-[1.65]">
          From booking to daily care - a clear, step-by-step process designed for your peace of
          mind.
        </p>
      </section>

      <section
        className="max-w-[1260px] mx-auto px-4 sm:px-8 py-16 sm:py-24"
        style={{ backgroundColor: BG, borderColor: BG }}
      >
        <div className="md:hidden relative overflow-hidden">
          <div className="absolute top-4 bottom-4 left-5 w-[3px]" style={{ backgroundColor: LIGHT_BLUE }} />
          <div className="space-y-10">
            {steps.map((item, index) => (
              <div key={item.step} className="relative pl-16">
                <div
                  className="absolute left-[6px] top-10 w-8 h-8 rounded-full flex items-center justify-center text-white text-lg font-bold border-4"
                  style={{ backgroundColor: BLUE, borderColor: "#e6efff" }}
                >
                  {index + 1}
                </div>
                <StepCard item={item} />
              </div>
            ))}
          </div>
        </div>

        <div className="hidden md:block relative step-timeline-wrap" style={{ borderColor: BG }}>
          <div
            className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-[4px]"
            style={{ backgroundColor: LIGHT_BLUE }}
          />
          <div className="space-y-14">
            {steps.map((item, index) => {
              const left = index % 2 === 0;
              return (
                <div
                  key={item.step}
                  className="step-timeline-row relative grid grid-cols-2 gap-12 lg:gap-16 xl:gap-20
 items-center border-0"
                  style={{ backgroundColor: "transparent", borderColor: BG, boxShadow: "none", outline: "none" }}
                >
                  <div className={left ? "col-start-1 flex justify-end" : "col-start-2 flex justify-start"}>
                    <StepCard item={item} />
                  </div>

                  <div
                    className="absolute left-1/2 -translate-x-1/2 w-14 h-14 rounded-full flex items-center justify-center text-white text-3xl font-bold border-4"
                    style={{ backgroundColor: BLUE, borderColor: "#e6efff" }}
                  >
                    {index + 1}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section
        className="relative overflow-hidden px-4 py-16 sm:py-24"
        style={{
          background:
            "radial-gradient(circle at 15% 18%, #f9fcff 0%, #edf4ff 35%, #e9eef7 63%, #e6ebf3 100%)",
        }}
      >
        <div className="pointer-events-none absolute inset-0">
          <div
            className="after-booking-orb-1 absolute -top-20 -left-24 h-[340px] w-[340px] rounded-full blur-3xl opacity-70"
            style={{ background: "radial-gradient(circle, #ffffff 0%, #dfeeff 80%)" }}
          />
          <div
            className="after-booking-orb-2 absolute top-[28%] -right-24 h-[420px] w-[420px] rounded-full blur-3xl opacity-70"
            style={{ background: "radial-gradient(circle, #fff3ea 0%, #e3ebff 80%)" }}
          />
          <div
            className="after-booking-orb-3 absolute -bottom-24 left-[33%] h-[360px] w-[360px] rounded-full blur-3xl opacity-60"
            style={{ background: "radial-gradient(circle, #ffffff 0%, #dde7fb 80%)" }}
          />
        </div>

        <div className="relative max-w-[1480px] mx-auto">
          <h2
            className="text-center font-bold tracking-tight text-[2rem] sm:text-[2.8rem] lg:text-[3.9rem]"
            style={{ color: TEXT_DARK }}
          >
            What Happens After Booking?
          </h2>
          <p className="mt-4 text-center text-[1rem] sm:text-[1.45rem]" style={{ color: TEXT_BODY }}>
            Complete transparency and accountability in every interaction.
          </p>

          <div className="mt-14 grid gap-6 sm:gap-8 md:grid-cols-2 xl:grid-cols-4">
            {afterBooking.map((item, index) => {
              const Icon = item.Icon;
              return (
                <article
                  key={item.title}
                  className="after-booking-card rounded-[18px] p-7 sm:p-8 border text-center min-h-[330px] lg:min-h-[360px] flex flex-col items-center transition-all duration-500 hover:-translate-y-2"
                  style={{
                    borderColor: "#d9e4f2",
                    background: "linear-gradient(145deg, #ffffff 0%, #f4f8ff 52%, #fdf0e5 100%)",
                    boxShadow: "0 14px 34px rgba(16, 35, 63, 0.12)",
                  }}
                >
                  <div className="after-booking-card-blob-1 absolute -top-10 -right-8 w-32 h-32 rounded-full bg-[#f1dfd7]/70" />
                  <div className="after-booking-card-blob-2 absolute -bottom-10 -left-8 w-28 h-28 rounded-full bg-[#dfe8ff]/70" />
                  <div
                    className="after-booking-icon-wrap mx-auto w-20 h-20 rounded-full flex items-center justify-center"
                    style={{
                      background: "linear-gradient(145deg, #e9f8f7 0%, #d3efec 52%, #bee5e1 100%)",
                      animationDelay: `${index * 0.2}s`,
                    }}
                  >
                    <Icon className="after-booking-icon" size={32} color="#0f0f0f" />
                  </div>
                  <h3
                    className="relative z-10 mt-7 text-[1.75rem] sm:text-[2.05rem] font-bold leading-[1.2]"
                    style={{ color: TEXT_DARK }}
                  >
                    {item.title}
                  </h3>
                  <p
                    className="relative z-10 mt-4 text-[1.05rem] sm:text-[1.15rem] leading-[1.55]"
                    style={{ color: TEXT_BODY }}
                  >
                    {item.desc}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>
      <style>
        {`
          @keyframes heroOrbFloatA {
            0% { transform: translate3d(0, 0, 0) scale(1); }
            50% { transform: translate3d(12px, -14px, 0) scale(1.05); }
            100% { transform: translate3d(0, 0, 0) scale(1); }
          }
          @keyframes heroOrbFloatB {
            0% { transform: translate3d(0, 0, 0) scale(1); }
            50% { transform: translate3d(-14px, 10px, 0) scale(0.96); }
            100% { transform: translate3d(0, 0, 0) scale(1); }
          }
          @keyframes heroFadeUp {
            0% { opacity: 0; transform: translateY(18px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes heroShine {
            0% { transform: translateX(-130%) skewX(-18deg); }
            100% { transform: translateX(200%) skewX(-18deg); }
          }
          .hero-premium {
            border-bottom: 1px solid #dbe6f3;
          }
          .hero-premium-orb-1 {
            background: radial-gradient(circle, #ffffff 0%, #d8e8ff 82%);
            animation: heroOrbFloatA 9.5s ease-in-out infinite;
          }
          .hero-premium-orb-2 {
            background: radial-gradient(circle, #fff2e5 0%, #dce8ff 78%);
            animation: heroOrbFloatB 11s ease-in-out infinite;
          }
          .hero-premium-grid {
            background-image:
              linear-gradient(to right, rgba(75, 106, 155, 0.08) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(75, 106, 155, 0.08) 1px, transparent 1px);
            background-size: 58px 58px;
            mask-image: radial-gradient(circle at center, black 35%, transparent 82%);
            -webkit-mask-image: radial-gradient(circle at center, black 35%, transparent 82%);
          }
          .hero-premium-pill {
            color: #65cbc6;
            background: rgba(255, 255, 255, 0.86);
            border: 1px solid #cfe0fb;
            letter-spacing: 0.01em;
            box-shadow: 0 6px 16px rgba(26, 49, 83, 0.08);
            animation: heroFadeUp 0.6s ease-out both;
          }
          .hero-premium-title {
            color: #10213f;
            line-height: 1.12;
            text-wrap: balance;
            text-shadow: 0 1px 0 rgba(255, 255, 255, 0.78);
            animation: heroFadeUp 0.65s ease-out both;
            animation-delay: 0.08s;
          }
          .hero-premium-sub {
            color: #3b5575;
            font-weight: 500;
            letter-spacing: 0.005em;
            animation: heroFadeUp 0.72s ease-out both;
            animation-delay: 0.14s;
            position: relative;
            overflow: hidden;
          }
          .hero-premium-sub::after {
            content: "";
            position: absolute;
            top: 0;
            left: -35%;
            width: 28%;
            height: 100%;
            background: linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,0.45), rgba(255,255,255,0));
            transform: skewX(-18deg);
            animation: heroShine 2.8s ease 0.9s 1;
            pointer-events: none;
          }
          .step-timeline-wrap,
          .step-timeline-row {
            border-color: ${BG} !important;
            box-shadow: none !important;
            outline: none !important;
            background: transparent !important;
          }
          @keyframes stepCardFloatA {
            0% { transform: translate3d(0, 0, 0) scale(1); }
            50% { transform: translate3d(9px, -7px, 0) scale(1.03); }
            100% { transform: translate3d(0, 0, 0) scale(1); }
          }
          @keyframes stepCardFloatB {
            0% { transform: translate3d(0, 0, 0) scale(1); }
            50% { transform: translate3d(-10px, 8px, 0) scale(1.02); }
            100% { transform: translate3d(0, 0, 0) scale(1); }
          }
          @keyframes stepCardShimmer {
            0% { transform: translateX(-120%) skewX(-14deg); }
            100% { transform: translateX(220%) skewX(-14deg); }
          }
          .step-card {
            backdrop-filter: blur(1.2px);
          }
          .step-card::before {
            content: "";
            position: absolute;
            top: 0;
            left: -45%;
            width: 42%;
            height: 100%;
            background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.48) 50%, rgba(255,255,255,0) 100%);
            transform: skewX(-14deg);
            pointer-events: none;
            opacity: 0;
          }
          .step-card:hover::before {
            animation: stepCardShimmer 1.05s ease;
            opacity: 1;
          }
          .step-card:hover {
            transform: translateY(-6px);
            border-color: #c6d8ed;
            box-shadow: 0 22px 46px rgba(17, 35, 62, 0.18);
          }
          .step-card-orb-a {
            animation: stepCardFloatA 8.8s ease-in-out infinite;
          }
          .step-card-orb-b {
            animation: stepCardFloatB 9.4s ease-in-out infinite;
          }
          .step-card-media {
            position: relative;
          }
          .step-card-media::after {
            content: "";
            position: absolute;
            inset: 0;
            pointer-events: none;
            background: linear-gradient(to top, rgba(15, 27, 53, 0.2), rgba(15, 27, 53, 0) 50%);
          }
          .step-card-video {
            transition: transform 0.7s ease;
            will-change: transform;
          }
          .step-card:hover .step-card-video {
            transform: scale(1.055);
          }
          @keyframes afterBookingFloatA {
            0% { transform: translate3d(0, 0, 0) scale(1); }
            50% { transform: translate3d(16px, -14px, 0) scale(1.04); }
            100% { transform: translate3d(0, 0, 0) scale(1); }
          }
          @keyframes afterBookingFloatB {
            0% { transform: translate3d(0, 0, 0) scale(1); }
            50% { transform: translate3d(-18px, 10px, 0) scale(0.98); }
            100% { transform: translate3d(0, 0, 0) scale(1); }
          }
          @keyframes afterBookingFloatC {
            0% { transform: translate3d(0, 0, 0) scale(1); }
            50% { transform: translate3d(12px, 14px, 0) scale(1.03); }
            100% { transform: translate3d(0, 0, 0) scale(1); }
          }
          .after-booking-orb-1 { animation: afterBookingFloatA 11s ease-in-out infinite; }
          .after-booking-orb-2 { animation: afterBookingFloatB 13s ease-in-out infinite; }
          .after-booking-orb-3 { animation: afterBookingFloatC 12s ease-in-out infinite; }
          .after-booking-card {
            position: relative;
            overflow: hidden;
          }
          .after-booking-card-blob-1,
          .after-booking-card-blob-2 {
            pointer-events: none;
            transition: transform 0.5s ease;
          }
          .after-booking-card::before {
            content: "";
            position: absolute;
            inset: -160% -40%;
            background: linear-gradient(120deg, transparent 35%, rgba(255, 255, 255, 0.45) 50%, transparent 65%);
            transform: rotate(7deg) translateX(-65%);
            transition: transform 0.9s ease;
            pointer-events: none;
          }
          .after-booking-card:hover::before {
            transform: rotate(7deg) translateX(65%);
          }
          .after-booking-card:hover .after-booking-card-blob-1 {
            transform: translate3d(-4px, 4px, 0) scale(1.04);
          }
          .after-booking-card:hover .after-booking-card-blob-2 {
            transform: translate3d(4px, -3px, 0) scale(1.04);
          }
          .after-booking-card:hover {
            box-shadow: 0 16px 34px rgba(28, 42, 61, 0.16);
            border-color: #c9d8ea;
          }
          @keyframes afterBookingIconFloat {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-6px); }
            100% { transform: translateY(0px); }
          }
          @keyframes afterBookingIconPulse {
            0% { box-shadow: 0 0 0 0 rgba(58, 175, 169, 0.30); }
            70% { box-shadow: 0 0 0 14px rgba(58, 175, 169, 0); }
            100% { box-shadow: 0 0 0 0 rgba(58, 175, 169, 0); }
          }
          .after-booking-icon-wrap {
            position: relative;
            z-index: 1;
            animation: afterBookingIconFloat 2.8s ease-in-out infinite,
              afterBookingIconPulse 3.6s ease-out infinite;
          }
          .after-booking-icon {
            animation: afterBookingFloatC 3.2s ease-in-out infinite;
          }
        `}
      </style>
    </div>
  );
};

export default HowItWork;


